import { Link, useSearchParams } from 'react-router'

import { Button } from '@/components/ui/button'
import { AuthCard } from '@/layouts/AuthLayout'
import { ErrorLayout } from '@/layouts/ErrorLayout'

/**
 * The following errors are passed as error query parameters to the default or overridden error page.
 *
 * [Documentation](https://next-auth.js.org/configuration/pages#error-page) */
export type ErrorType =
  | 'default'
  | 'configuration'
  | 'bad gateway'
  | 'maintenance'
  | 'accessdenied'
  | 'verification'

export interface ErrorProps {
  url?: URL
  error?: ErrorType
}

interface ErrorView {
  code?: number
  heading: string
  message?: string
  signin?: boolean
}

const errors: Record<ErrorType, ErrorView> = {
  default: {
    heading: 'Něco se pokazilo',
    message: 'Přihlášení se nepodařilo dokončit. Zkuste to prosím znovu.',
    signin: true,
  },
  configuration: {
    code: 500,
    heading: 'Chyba na serveru',
    message: 'V nastavení serveru je chyba. Víc řeknou serverové logy.',
  },
  'bad gateway': {
    code: 502,
    heading: 'Server teď neodpovídá',
    message: 'O chybě už víme a pracujeme na nápravě. Omlouváme se za potíže.',
    signin: true,
  },
  maintenance: {
    code: 503,
    heading: 'Probíhá údržba',
    message: 'Systém je v režimu údržby. Zkuste to prosím za chvíli.',
  },
  accessdenied: {
    code: 403,
    heading: 'Přístup odepřen',
    message: 'K přihlášení nemáte oprávnění.',
    signin: true,
  },
  verification: {
    code: 403,
    heading: 'Odkaz už neplatí',
    message: 'Odkaz pro přihlášení už byl použit, nebo mu vypršela platnost.',
    signin: true,
  },
}

/** Chybová stránka přihlášení. */
export default function ErrorPage() {
  const [searchParams] = useSearchParams()
  const error = searchParams.get('error')

  const view = (error && errors[error.toLowerCase() as ErrorType]) || errors.default

  return (
    <ErrorLayout>
      <title>{view.heading}</title>
      <AuthCard
        title={
          <>
            {view.code && <span className="mb-2 block text-xs font-normal text-muted-foreground tabular">Chyba {view.code}</span>}
            {view.heading}
          </>
        }
        description={view.message}
      >
        {view.signin ? (
          <Button asChild className="w-full">
            <Link to="/auth/login">Přihlásit se</Link>
          </Button>
        ) : (
          <Button variant="outline" className="w-full" onClick={() => window.location.reload()}>
            Zkusit znovu
          </Button>
        )}
      </AuthCard>
    </ErrorLayout>
  )
}
